import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  FileText,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  UserRound,
} from 'lucide-react';
import { appelApi, ErreurApi } from '../../lib/api';
import { Modale } from '../ui/Modale';
import { VisionneusePiece } from '../dossier/VisionneusePiece';
import { urlDocumentGenere } from '../../lib/useDocuments';
import { BoutonMint, BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';
import type { SituationContrat } from '../../lib/useContrats';
import {
  ChoixMaitreApprentissage,
  type MaitreApprentissageProposable,
  type SaisieNouveauMaitre,
} from './ChoixMaitreApprentissage';

/**
 * Contrôle du CERFA avant la validation définitive d'un contrat.
 *
 * L'écran ne valide pas puis découvre : il montre d'abord ce que le CERFA
 * exige, ce que le dossier porte déjà, et ce qui manque. Les informations
 * manquantes se saisissent ici, et rejoignent la fiche de l'apprenant, de
 * l'entreprise ou de l'antenne — elles ne seront pas redemandées au contrat
 * suivant.
 *
 * À l'ouverture, l'API hydrate chaque rubrique depuis les fiches liées à
 * l'immersion (employeur, apprenti, antenne et formation). Chaque valeur reste
 * dans un champ éditable afin qu'un contrat puisse conserver son exception.
 */

/**
 * Rubriques dont la valeur décide de ce que le CERFA exige ailleurs.
 *
 * La règle vit côté serveur, et elle y reste : le front ne la recopie pas, il
 * redemande le contrôle quand l'une de ces rubriques change. Sans cela, saisir
 * un numéro de contrat précédent n'afficherait la date d'effet d'avenant
 * qu'après réouverture de la popup — et la validation échouerait sur une
 * rubrique jamais montrée.
 */
const RUBRIQUES_PILOTES = new Set([
  'apprenant.dateNaissance',
  'contrat.dateDebut',
  'contrat.dateFin',
  'entreprise.secteurEmployeur',
  'contrat.numeroContratPrecedent',
  'contrat.dateEffetAvenant',
  // Répondre « oui » fait apparaître un bloc entier, et « non » le fait
  // disparaître : le serveur seul sait lequel, et il faut le lui redemander.
  'contrat.secondTuteurPrevu',
  'contrat.lieuFormationEstCfa',
]);

/** Ordre des blocs à l'écran : celui du formulaire officiel. */
const ORDRE_BLOCS = [
  'Employeur',
  'Apprenti(e)',
  'Représentant légal',
  "Maître d'apprentissage",
  'Contrat',
  'Formation',
  'CFA',
];

/**
 * Données calculées ou portées par les fiches liées : pas de ressaisie contrat.
 *
 * Le salaire brut à l'embauche n'en fait **pas** partie : il est proposé par le
 * calcul, mais il se corrige — minimum de branche, dérogation — et une rubrique
 * obligatoire masquée était réclamée sans qu'on puisse la saisir.
 */
const RUBRIQUES_AUTOMATIQUES = new Set([
  'contrat.dateConclusion',
  'contrat.lieuSignature',
  'contrat.lieuFormationEstCfa',
  'contrat.attestationPieces',
  'contrat.dateDebutFormationCfa',
  'contrat.dateFinExamens',
]);

export interface RubriqueCerfa {
  cle: string;
  bloc: string;
  libelle: string;
  saisie: 'texte' | 'date' | 'entier' | 'montant' | 'booleen' | 'courriel' | 'telephone' | 'choix';
  aide?: string;
  valeur: string | null;
  requise: boolean;
  /** Fausse quand la rubrique ne concerne pas ce contrat : elle n'est pas affichée. */
  applicable: boolean;
  manquante: boolean;
  /** Valeur sans laquelle la rubrique reste à compléter, même renseignée. */
  valeurAttendue?: string;
  options?: { code: string; libelle: string }[];
  /** Valeurs proposées sans fermer la saisie (nomenclatures non arbitrées). */
  suggestions?: string[];
  /** Valeur proposée par le calcul, pas encore enregistrée. */
  proposee?: boolean;
}

/** Une ligne de la grille de rémunération, telle que l'API l'échange. */
export interface LigneRemuneration {
  annee: number;
  periode: number;
  dateDebut: string;
  dateFin: string;
  pourcentage: number;
  base: 'SMIC' | 'MINIMUM_CONVENTIONNEL';
  /** Montant mensuel brut calculé par le serveur, sur le SMIC du référentiel. */
  montant?: number | null;
  /** Vraie tant que la ligne vient du barème et n'a pas été enregistrée. */
  proposee?: boolean;
  /** Taux que le barème donne à cette case, pour montrer l'écart d'une correction. */
  pourcentageBareme?: number | null;
}

export interface ControleCerfa {
  immersionId: string;
  candidat: string;
  entreprise: string;
  formation: string | null;
  /** `creation` tant qu'aucun contrat n'existe, `correction` ensuite. */
  mode: 'creation' | 'correction';
  contratId: string | null;
  /** CERFA déjà produit, à relire avant de le corriger. */
  documentCerfaId: string | null;
  brouillonEnregistre: boolean;
  /** État du contrat, calculé par l'API comme pour la liste. */
  situation: SituationContrat;
  complet: boolean;
  rubriques: RubriqueCerfa[];
  avertissements: string[];
  remunerations: LigneRemuneration[];
  /** Années contractuelles couvertes par les dates du contrat. */
  anneesContrat: number;
  /** SMIC mensuel de référence à la date de début du contrat. */
  referenceRemuneration: { mensuel: number | null };
  /** D'où vient la durée affichée : saisie au contrat, ou horaires de la formation. */
  dureeHebdoSource?: 'contrat' | 'formation' | null;
  /** Durée hebdomadaire déduite des horaires de la formation. */
  dureeHebdomadaireFormation: { heures: number; minutes: number; libelle: string } | null;
  /** Fiches de l'employeur du contrat, avec la charge de chacune sur sa période. */
  maitresApprentissage: MaitreApprentissageProposable[];
  /** Maître retenu, `null` tant qu'il reste à choisir ou à créer. */
  maitreApprentissageId: string | null;
  /** Période sur laquelle la charge des maîtres est jugée. */
  periodeCharge: { debut: string; fin: string; depuisLeContrat: boolean };
}

/** Rubriques du premier maître d'apprentissage : elles se lisent sur sa fiche. */
const estRubriqueMaitre = (cle: string): boolean => cle.startsWith('tuteur.');

/** Valeurs compatibles avec les champs contrôlés de la modale. */
type ValeursFormulaireCerfa = Record<string, string>;

const CLES_NUMERIQUES_CONTRAT = [
  'contrat.dureeHebdoHeures',
  'contrat.dureeHebdoMinutes',
  'contrat.remunerationBrute',
] as const;

/**
 * Les rubriques de l'API sont textuelles, mais leur origine DB peut être un
 * `Int` ou un `Decimal`. Normaliser ici garantit qu'un input contrôlé ne reçoit
 * jamais `null`, `undefined`, ni une représentation non numérique.
 */
function valeurNumeriqueFormulaire(
  valeur: string | null | undefined,
  decimales?: number,
): string {
  if (valeur === null || valeur === undefined || valeur.trim() === '') return '';
  const nombre = Number(valeur.replace(',', '.'));
  if (!Number.isFinite(nombre)) return '';
  return decimales === undefined ? String(nombre) : nombre.toFixed(decimales);
}

/**
 * Ligne telle que l'API l'attend : le montant n'en fait pas partie.
 *
 * Il est **calculé** par le serveur depuis le pourcentage et le SMIC du
 * référentiel. Le renvoyer laisserait croire qu'il se saisit, et deux montants
 * pour un même taux finiraient par se contredire.
 */
function ligneEnvoyee(ligne: LigneRemuneration): {
  annee: number;
  periode: number;
  dateDebut: string;
  dateFin: string;
  pourcentage: number;
  base: LigneRemuneration['base'];
} {
  return {
    annee: ligne.annee,
    periode: ligne.periode,
    dateDebut: ligne.dateDebut,
    dateFin: ligne.dateFin,
    pourcentage: ligne.pourcentage,
    base: ligne.base,
  };
}

/** Date locale attendue par `<input type="date">`, sans décalage UTC. */
function dateDuJourPourFormulaire(date = new Date()): string {
  const mois = String(date.getMonth() + 1).padStart(2, '0');
  const jour = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${mois}-${jour}`;
}

/**
 * Mapping unique des données API vers les valeurs affichées par le formulaire.
 * Les informations générales et le dépôt restent des textes, tandis que les
 * trois nombres du bloc Contrat sont explicitement normalisés.
 */
function preparerValeursInitialesCerfa(
  rubriques: RubriqueCerfa[],
  dateSignature: string,
): ValeursFormulaireCerfa {
  const valeurs = Object.fromEntries(
    rubriques.map(({ cle, valeur }) => [cle, valeur ?? '']),
  ) as ValeursFormulaireCerfa;

  for (const cle of CLES_NUMERIQUES_CONTRAT) {
    // Un montant garde ses centimes : « 1867.1 » se lirait mal à côté des
    // montants de la grille.
    valeurs[cle] = valeurNumeriqueFormulaire(
      valeurs[cle],
      cle === 'contrat.remunerationBrute' ? 2 : undefined,
    );
  }

  // Une signature déjà portée par le contrat est historique. Seule la
  // création reçoit la date du jour ; rouvrir un ancien contrat ne la réécrit
  // jamais silencieusement.
  if (!valeurs['contrat.dateConclusion']) {
    valeurs['contrat.dateConclusion'] = dateSignature;
  }

  return {
    ...valeurs,
    'site.uai': valeurs['site.uai'] ?? '',
    'site.siret': valeurs['site.siret'] ?? '',
    'site.numeroVoie': valeurs['site.numeroVoie'] ?? '',
    'site.complementAdresse': valeurs['site.complementAdresse'] ?? '',
    'depot.nomOrganisme': valeurs['depot.nomOrganisme'] ?? '',
    'depot.siretOrganisme': valeurs['depot.siretOrganisme'] ?? '',
    'depot.numeroDepot': valeurs['depot.numeroDepot'] ?? '',
    'depot.numeroAvenant': valeurs['depot.numeroAvenant'] ?? '',
  };
}

/** La date proposée doit aussi être envoyée si le contrat est validé sans retouche. */
function completerDateSignature(
  saisies: ValeursFormulaireCerfa,
  _dateSignature: string,
): ValeursFormulaireCerfa {
  // La proposition est faite par le service lors de la création. Ne pas
  // renvoyer une date par défaut ici : sur une correction elle écraserait la
  // date historique du contrat.
  return saisies;
}

interface Props {
  immersionId: string | null;
  onFermer: () => void;
  /** Le même parcours peut être rendu dans une page dédiée. */
  integree?: boolean;
  /** Appelée après une validation ou une régénération, pour recharger la liste. */
  onValide: (message: string) => void;
  /** Appelée après un enregistrement de brouillon : la popup, elle, reste ouverte. */
  onBrouillonEnregistre?: (message: string) => void;
}

export function ModaleControleCerfa({
  immersionId,
  onFermer,
  integree = false,
  onValide,
  onBrouillonEnregistre,
}: Props): JSX.Element | null {
  const [controle, setControle] = useState<ControleCerfa | null>(null);
  const [saisies, setSaisies] = useState<Record<string, string>>({});
  // La grille vit à part des rubriques : c'est un tableau de périodes, pas une
  // valeur par clé. `null` veut dire « pas encore touchée » — c'est alors celle
  // que l'API renvoie qui est affichée, proposée par le barème.
  const [grille, setGrille] = useState<LigneRemuneration[] | null>(null);
  const [chargement, setChargement] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Verrou du geste qui engage, et lui seul : il se pose après une validation
  // ou une correction aboutie, pour qu'un second clic ne rejoue pas le même
  // envoi. Il ne se déduit **pas** de la présence d'une demande de signature :
  // un contrat validé en a toujours une, et son CERFA reste corrigeable.
  const [gesteAbouti, setGesteAbouti] = useState(false);
  const [sectionActive, setSectionActive] = useState<string>('Employeur');
  /** Une nouvelle fiche de maître est en cours de saisie, au-dessus de son bloc. */
  const [creationMaitre, setCreationMaitre] = useState(false);
  /** CERFA ouvert en lecture par-dessus l'écran de saisie. */
  const [documentApercu, setDocumentApercu] = useState<string | null>(null);
  const dateSignatureInitiale = dateDuJourPourFormulaire();

  const charger = useCallback(
    async (
      complements: Record<string, string>,
      maitre: string | null,
      _remunerations: LigneRemuneration[] | null,
      recalculer = false,
    ): Promise<ControleCerfa | null> => {
      if (!immersionId) return null;
      setChargement(true);
      try {
        const etat = await appelApi<ControleCerfa>(`/contrats/${immersionId}/cerfa/controle`, {
          methode: 'POST',
          corps: {
            complements: completerDateSignature(complements, dateSignatureInitiale),
            // Le maître choisi à l'écran voyage avec chaque contrôle : sans lui,
            // le serveur reviendrait à celui du brouillon ou du contrat.
            ...(maitre ? { maitreApprentissageId: maitre } : {}),
            ...(_remunerations && _remunerations.length > 0
              ? { remunerations: _remunerations.map(ligneEnvoyee) }
              : {}),
            ...(recalculer ? { recalculer: true } : {}),
          },
        });
        setControle(etat);
        setErreur(null);
        return etat;
        // Un CERFA déjà généré peut être rouvert pour recueillir une signature
        // laissée en attente. Dans ce cas il n'y a pas de nouvelle réponse de
        // validation qui nous donnerait l'identifiant de la demande : on la
        // retrouve donc directement, sans faire sortir l'utilisateur de la
        // modale du contrat.
      } catch (e) {
        setErreur(e instanceof ErreurApi ? e.message : 'Le contrôle du CERFA a échoué.');
        return null;
      } finally {
        setChargement(false);
      }
    },
    [immersionId, dateSignatureInitiale],
  );

  // À l'ouverture seulement : ensuite, le recalcul de ce qui manque se fait à
  // l'écran, sans aller-retour à chaque frappe.
  useEffect(() => {
    if (!immersionId) {
      setControle(null);
      setSaisies({});
      setGrille(null);
      setErreur(null);
      setGesteAbouti(false);
      setSectionActive('Employeur');
      return;
    }
    void charger({}, null, null);
  }, [immersionId, charger]);

  /** Grille affichée : celle qu'on modifie, ou celle que l'API renvoie. */
  const grilleAffichee = grille ?? controle?.remunerations ?? [];

  // Les données renvoyées au chargement sont la valeur hydratée depuis les
  // fiches liées à l'immersion. `saisies` ne contient que les corrections
  // locales : elles prennent le dessus sans modifier la fiche avant l'action
  // explicite « Enregistrer » ou « Valider ».
  const valeursInitiales = useMemo(
    () => preparerValeursInitialesCerfa(controle?.rubriques ?? [], dateSignatureInitiale),
    [controle, dateSignatureInitiale],
  );
  const valeurDe = useCallback(
    (rubrique: RubriqueCerfa): string => saisies[rubrique.cle] ?? valeursInitiales[rubrique.cle] ?? '',
    [saisies, valeursInitiales],
  );

  /**
   * Ce qui manque encore, recalculé à la frappe.
   *
   * L'API garde le dernier mot — elle refait ce contrôle à la validation —
   * mais l'écran doit réagir immédiatement, sinon la liste des manques reste
   * fausse tant qu'on n'a pas renvoyé la requête.
   */
  /**
   * Une rubrique reste à compléter quand elle est vide — ou, pour une
   * déclaration comme l'attestation de l'employeur, quand la réponse n'est pas
   * celle qu'exige le dépôt. « Non » est une valeur : sans cette règle, le
   * refus n'apparaîtrait qu'après avoir cliqué sur « Valider ».
   */
  const estACompleter = useCallback(
    (rubrique: RubriqueCerfa): boolean => {
      if (!rubrique.requise) return false;
      const valeur = saisies[rubrique.cle] ?? valeursInitiales[rubrique.cle] ?? '';
      return !valeur || (rubrique.valeurAttendue !== undefined && valeur !== rubrique.valeurAttendue);
    },
    [saisies, valeursInitiales],
  );

  const manquantes = useMemo(
    () => (controle?.rubriques ?? []).filter(estACompleter),
    [controle, estACompleter],
  );

  /**
   * Tant qu'aucun maître n'est retenu, ses rubriques ne se saisissent pas : elles
   * se lisent sur la fiche qu'on choisira. Elles ne sont donc pas nommées une à
   * une, mais en un seul manque qui mène au choix.
   */
  const maitreAChoisir = controle !== null && controle.maitreApprentissageId === null;
  const manquantesNommees = useMemo(() => {
    if (!maitreAChoisir) return manquantes;
    let dejaNomme = false;
    return manquantes.flatMap((rubrique) => {
      if (!estRubriqueMaitre(rubrique.cle)) return [rubrique];
      if (dejaNomme) return [];
      dejaNomme = true;
      return [{ ...rubrique, libelle: 'à choisir ou créer' }];
    });
  }, [manquantes, maitreAChoisir]);

  /** Dénominateur de l'avancement : les rubriques que le dépôt exige vraiment. */
  const totalRequises = useMemo(
    () => (controle?.rubriques ?? []).filter((rubrique) => rubrique.applicable && rubrique.requise).length,
    [controle],
  );

  const blocs = useMemo(() => {
    const parBloc = new Map<string, RubriqueCerfa[]>();
    for (const rubrique of controle?.rubriques ?? []) {
      // Obligatoires et facultatives sont toutes saisissables. Seules les
      // rubriques hors de leur cas — représentant légal d'un majeur, second
      // maître d'apprentissage non prévu, lieu de formation identique au CFA —
      // ne sont pas affichées : un champ vide laisserait croire qu'il reste à
      // remplir.
      if (!rubrique.applicable || rubrique.bloc === 'CFA' || rubrique.bloc === 'Dépôt' || RUBRIQUES_AUTOMATIQUES.has(rubrique.cle)) continue;
      // Sans maître retenu, ses rubriques n'ont pas de fiche où se lire : le
      // choix, en tête du bloc, vient d'abord.
      if (controle?.maitreApprentissageId === null && estRubriqueMaitre(rubrique.cle)) continue;
      parBloc.set(rubrique.bloc, [...(parBloc.get(rubrique.bloc) ?? []), rubrique]);
    }
    return [...parBloc.entries()].sort(
      (a, b) => rangBloc(a[0]) - rangBloc(b[0]),
    );
  }, [controle]);

  /**
   * Se tromper de maître d'apprentissage sur un CERFA n'a rien d'anodin :
   * quand l'entreprise en compte plusieurs, le choix est attendu.
   */
  // La signature ne devient une étape que lorsque le CERFA auquel elle se
  // rattache existe réellement. On ne montre jamais un faux pavé d'attente.
  const sections = useMemo(() => [
    ...blocs.map(([bloc]) => bloc),
    'Rémunération',
  ], [blocs]);
  const blocActif = blocs.find(([bloc]) => bloc === sectionActive);
  const indexSection = Math.max(0, sections.indexOf(sectionActive));

  /**
   * Redemande au serveur ce qui est exigé, saisies en cours comprises.
   *
   * Appelé quand une rubrique pilote est quittée, pas à chaque frappe : le
   * contrôle lit la base, et le relancer sur chaque caractère ferait clignoter
   * le formulaire sans rien apprendre de plus.
   */
  const maitreRetenu = controle?.maitreApprentissageId ?? null;
  const recontroler = useCallback(() => {
    // La grille corrigée part avec : sans elle, le recontrôle rendrait la
    // proposition du barème et effacerait la correction en cours.
    void charger(saisies, maitreRetenu, grille);
  }, [charger, saisies, grille, maitreRetenu]);

  /**
   * Retient une autre fiche de maître d'apprentissage.
   *
   * Les corrections faites sur les rubriques du maître précédent sont
   * abandonnées : envoyées avec le nouveau choix, elles réécriraient sa fiche
   * au nom de l'autre. Les rubriques se relisent sur la fiche choisie.
   */
  const choisirMaitre = useCallback(
    (maitreId: string) => {
      const sansMaitre = Object.fromEntries(
        Object.entries(saisies).filter(([cle]) => !estRubriqueMaitre(cle)),
      );
      setGesteAbouti(false);
      setSaisies(sansMaitre);
      void charger(sansMaitre, maitreId, grille);
    },
    [charger, saisies, grille],
  );

  /** Crée la fiche dans l'entreprise du contrat, puis la retient. */
  const creerMaitre = async (saisie: SaisieNouveauMaitre): Promise<void> => {
    if (!immersionId) return;
    const cree = await appelApi<{ id: string; message: string }>(
      `/contrats/${immersionId}/maitres-apprentissage`,
      { methode: 'POST', corps: saisie },
    );
    choisirMaitre(cree.id);
  };

  /**
   * Met la saisie de côté sans valider.
   *
   * Toujours disponible, même sur un formulaire incomplet : c'est précisément
   * le cas où elle sert. La popup reste ouverte, et l'état qu'elle affiche est
   * désormais celui de la base — ce qui a été rangé dans les fiches n'apparaît
   * plus comme une saisie locale.
   */
  const enregistrer = async (): Promise<void> => {
    if (!immersionId) return;
    setEnregistrement(true);
    setErreur(null);
    try {
      const etat = await appelApi<ControleCerfa>(`/contrats/${immersionId}/cerfa/brouillon`, {
        methode: 'POST',
        corps: {
          complements: completerDateSignature(saisies, dateSignatureInitiale),
          ...(maitreRetenu ? { maitreApprentissageId: maitreRetenu } : {}),
          ...(grille ? { remunerations: grille.map(ligneEnvoyee) } : {}),
        },
      });
      setControle(etat);
      setSaisies({});
      setGrille(null);
      // La grille revient de la base : la garder en local la figerait sur ce
      // qu'on venait d'envoyer, y compris si le serveur l'a corrigée.
      onBrouillonEnregistre?.(
        etat.mode === 'correction'
          ? 'Modifications enregistrées sur le contrat. Régénérez le CERFA pour les y porter.'
          : 'Saisie enregistrée : le contrat reste à valider.',
      );
    } catch (e) {
      setErreur(
        e instanceof ErreurApi ? e.message : "La saisie n'a pas pu être enregistrée.",
      );
    } finally {
      setEnregistrement(false);
    }
  };

  const valider = async (): Promise<void> => {
    if (!immersionId || !controle) return;
    setEnvoi(true);
    setErreur(null);
    const chemin = controle.mode === 'correction' ? 'cerfa/regenerer' : 'valider';
    const corps = {
      complements: completerDateSignature(saisies, dateSignatureInitiale),
      ...(maitreRetenu ? { maitreApprentissageId: maitreRetenu } : {}),
      // La grille corrigée fait foi : c'est elle qui part au formulaire, et
      // elle est enregistrée sur le contrat. Laissée telle que proposée, elle
      // n'est pas renvoyée — le serveur applique alors sa propre grille depuis
      // les dates et l'âge, et les deux disent la même chose.
      ...(grille ? { remunerations: grille.map(ligneEnvoyee) } : {}),
    };
    try {
      // Le même contrôle que celui du serveur est rejoué juste avant le geste
      // final. Ainsi une donnée devenue conditionnelle ou une période invalide
      // ne se transforme jamais en un 400 opaque dans la console.
      const verification = await appelApi<ControleCerfa>(`/contrats/${immersionId}/cerfa/controle`, {
        methode: 'POST',
        corps,
      });
      setControle(verification);
      const reponse = await appelApi<{ message: string; demandeSignatureId?: string | null }>(`/contrats/${immersionId}/${chemin}`, {
        methode: 'POST',
        corps,
      });
      // La personne reste sur le contrat, et ce qu'elle voit est relu en
      // base : mode correction, CERFA à ouvrir, état du contrat. Sans cette
      // relecture, la page gardait l'état d'avant la génération jusqu'à ce
      // qu'on l'actualise.
      setSaisies({});
      setGrille(null);
      await charger({}, null, null);
      setGesteAbouti(true);
      onValide(reponse.message);
    } catch (e) {
      // Les saisies sont conservées : la personne corrige et relance, elle ne
      // recommence pas.
      setErreur(
        e instanceof ErreurApi
          ? e.message
          : "Le CERFA n'a pas pu être généré. Le contrat n'a pas été validé.",
      );
    } finally {
      setEnvoi(false);
    }
  };

  if (!immersionId) return null;

  /**
   * Mène droit au champ resté vide : la section s'ouvre, le champ prend le
   * focus. Nommer un manque sans y conduire oblige à parcourir les blocs.
   */
  const allerAuChamp = (rubrique: RubriqueCerfa): void => {
    setSectionActive(rubrique.bloc);
    const identifiant = `cerfa-${rubrique.cle.replace(/\./g, '-')}`;
    // Le champ n'existe qu'après le rendu de sa section. Une rubrique du
    // maître encore à choisir mène au choix lui-même.
    window.setTimeout(() => {
      const champ =
        document.getElementById(identifiant) ??
        (estRubriqueMaitre(rubrique.cle) ? document.getElementById('cerfa-choix-maitre') : null);
      champ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      champ?.focus({ preventScroll: true });
    }, 0);
  };

  const titre =
    controle?.mode === 'correction'
      ? "Corriger le contrat d'apprentissage"
      : "Nouveau contrat d'apprentissage";

  return (
    <Modale
      titre={titre}
      sousTitre={
        controle
          ? `${controle.candidat} · ${controle.entreprise}${controle.formation ? ` · ${controle.formation}` : ''}`
          : undefined
      }
      ouverte
      onFermer={onFermer}
      largeur="max-w-6xl"
      integree={integree}
    >
      {chargement && !controle ? (
        <p className="flex items-center gap-2 rounded-2xl bg-white p-6 text-sm text-codialis-gray shadow-card">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Lecture du dossier…
        </p>
      ) : !controle ? (
        <MessageErreur message={erreur ?? 'Contrôle indisponible.'} />
      ) : (
        <div className="space-y-4">
          {/* En-tête : qui, où, quoi. Un formulaire administratif n'a pas
              besoin d'être vendu — il a besoin d'être situé. */}
          <header className="rounded-2xl bg-white shadow-card">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
              <div className="min-w-0">
                {/* Page dédiée : c'est le titre de l'écran (RGAA 9.1). */}
                {integree ? (
                  <h1 className="font-display text-xl font-bold text-codialis-navy sm:text-2xl">{titre}</h1>
                ) : (
                  <h2 className="font-display text-xl font-bold text-codialis-navy sm:text-2xl">{titre}</h2>
                )}
                <p className="mt-1 text-sm text-codialis-gray">
                  Formulaire CERFA 10103*14. Les données du dossier, de l’entreprise et de
                  l’antenne sont déjà reprises ; elles restent modifiables.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Corriger se décide sur le document produit, pas sur le
                    souvenir de ce qu'on a saisi. */}
                {controle.documentCerfaId ? (
                  <button
                    type="button"
                    onClick={() => setDocumentApercu(controle.documentCerfaId)}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50"
                  >
                    <FileText className="h-4 w-4" aria-hidden />
                    Ouvrir le CERFA
                  </button>
                ) : null}
                {/* Le même état que la liste, calculé au même endroit. */}
                <Pastille texte={controle.situation.libelle} ton={controle.situation.ton} />
                {controle.situation.detail ? (
                  <span className="text-xs text-codialis-gray">{controle.situation.detail}</span>
                ) : null}
              </div>
            </div>

            <dl className="grid grid-cols-1 gap-px bg-gray-100 sm:grid-cols-3">
              <LigneContexte icone={<UserRound className="h-4 w-4" aria-hidden />} libelle="Apprenti(e)" valeur={controle.candidat} />
              <LigneContexte icone={<Building2 className="h-4 w-4" aria-hidden />} libelle="Entreprise" valeur={controle.entreprise} />
              <LigneContexte icone={<CalendarDays className="h-4 w-4" aria-hidden />} libelle="Formation" valeur={controle.formation ?? 'À préciser'} />
            </dl>

            <Avancement
              manquantes={manquantes}
              nommees={manquantesNommees}
              total={totalRequises}
              mode={controle.mode}
              contratValide={controle.situation.etat !== 'A_VALIDER'}
              brouillonEnregistre={controle.brouillonEnregistre}
              onAllerAuChamp={allerAuChamp}
            />
          </header>

          {erreur ? <MessageErreur message={erreur} /> : null}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[13rem_minmax(0,1fr)]">
            {/* Sommaire : vertical au bureau, défilant au doigt sur tablette
                et téléphone. Le compteur dit où il reste à travailler. */}
            <nav aria-label="Sections du contrat" className="min-w-0 lg:sticky lg:top-4 lg:self-start">
              <ul className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
                {sections.map((section, index) => {
                  const reste = manquantes.filter((rubrique) => rubrique.bloc === section).length;
                  const active = sectionActive === section;
                  return (
                    <li key={section} className="min-w-fit lg:min-w-0">
                      <button
                        type="button"
                        onClick={() => setSectionActive(section)}
                        aria-current={active ? 'step' : undefined}
                        className={`flex w-full min-h-[40px] items-center gap-2 whitespace-nowrap rounded-xl border px-3 py-2 text-left text-sm font-semibold transition lg:whitespace-normal ${
                          active
                            ? 'border-codialis-navy bg-codialis-navy text-white'
                            : 'border-gray-200 bg-white text-codialis-navy hover:border-codialis-sky'
                        }`}
                      >
                        <span
                          className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                            active ? 'bg-white/20 text-white' : 'bg-gray-100 text-codialis-gray'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">{section}</span>
                        {reste > 0 ? (
                          <span className="shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">
                            {reste}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="min-w-0 space-y-4">
              {blocActif ? (
                <section className="rounded-2xl bg-white shadow-card">
                  <header className="border-b border-gray-100 px-4 py-3.5 sm:px-6 sm:py-4">
                    <h3 className="font-display text-lg font-bold text-codialis-navy">{blocActif[0]}</h3>
                    <p className="mt-0.5 text-sm text-codialis-gray">{descriptionBloc(blocActif[0])}</p>
                  </header>
                  <div className="grid grid-cols-1 gap-x-5 gap-y-4 p-4 sm:p-6 md:grid-cols-2">
                    {blocActif[0] === "Maître d'apprentissage" ? (
                      <ChoixMaitreApprentissage
                        entreprise={controle.entreprise}
                        maitres={controle.maitresApprentissage}
                        maitreId={controle.maitreApprentissageId}
                        periode={controle.periodeCharge}
                        onChoisir={choisirMaitre}
                        onCreer={creerMaitre}
                        onCreationOuverte={setCreationMaitre}
                        desactive={chargement || envoi || enregistrement}
                      />
                    ) : null}
                    {blocActif[1]
                      .filter((rubrique) => !(creationMaitre && estRubriqueMaitre(rubrique.cle)))
                      .map((rubrique) => (
                        <ChampRubrique
                          key={rubrique.cle}
                          rubrique={rubrique}
                          aCompleter={estACompleter(rubrique)}
                          valeur={valeurDe(rubrique)}
                          onChanger={(valeur) => {
                            // Corriger après avoir corrigé doit rester possible :
                            // une saisie de plus rouvre le geste.
                            setGesteAbouti(false);
                            setSaisies((precedentes) => ({ ...precedentes, [rubrique.cle]: valeur }));
                          }}
                          onQuitter={RUBRIQUES_PILOTES.has(rubrique.cle) ? recontroler : undefined}
                        />
                      ))}
                    {blocActif[1].some((rubrique) => rubrique.cle === 'contrat.dureeHebdoHeures') ? (
                      <ApercuDureeHebdo
                        heures={saisies['contrat.dureeHebdoHeures'] ?? valeursInitiales['contrat.dureeHebdoHeures'] ?? ''}
                        minutes={saisies['contrat.dureeHebdoMinutes'] ?? valeursInitiales['contrat.dureeHebdoMinutes'] ?? ''}
                        formation={controle.dureeHebdomadaireFormation ?? null}
                        saisieAuContrat={
                          saisies['contrat.dureeHebdoHeures'] !== undefined ||
                          saisies['contrat.dureeHebdoMinutes'] !== undefined ||
                          controle.dureeHebdoSource === 'contrat'
                        }
                      />
                    ) : null}
                  </div>
                </section>
              ) : null}

              {sectionActive === 'Rémunération' ? (
                <SectionRemuneration
                  lignes={grilleAffichee}
                  mensuelReference={controle.referenceRemuneration?.mensuel ?? null}
                  anneesContrat={controle.anneesContrat ?? 0}
                  onChanger={(lignes) => {
                    setGesteAbouti(false);
                    setGrille(lignes);
                  }}
                  onRetablir={() => {
                    // Revenir au barème, c'est demander au serveur de **recalculer**
                    // en ignorant la grille enregistrée — la relire ne rétablissait
                    // rien. La proposition devient la grille en cours : c'est elle
                    // qui partira à l'enregistrement et remplacera l'ancienne.
                    setGesteAbouti(false);
                    void charger(saisies, maitreRetenu, null, true).then((etat) => {
                      if (etat) setGrille(etat.remunerations);
                    });
                  }}
                />
              ) : null}

              {controle.avertissements.length > 0 ? (
                <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-amber-900">
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
                    À savoir avant de valider
                  </h3>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900">
                    {controle.avertissements.map((texte) => (
                      <li key={texte}>{texte}</li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </div>
          </div>

          {/* Barre d'actions collante : le contrôle fait plusieurs écrans de
              haut sur téléphone, et les gestes se trouvaient tout en bas.
              L'enregistrement reste possible sur un formulaire incomplet —
              c'est le cas où il sert — et reste séparé des gestes qui engagent. */}
          <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white/95 px-4 py-3 shadow-card backdrop-blur sm:px-6">
            <BoutonSecondaire onClick={enregistrer} disabled={envoi || enregistrement}>
              <Save className="mr-2 h-4 w-4" aria-hidden />
              {enregistrement ? 'Enregistrement…' : 'Enregistrer le brouillon'}
            </BoutonSecondaire>

            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSectionActive(sections[indexSection - 1]!)}
                disabled={indexSection === 0 || envoi || enregistrement}
                aria-label="Section précédente"
                className="grid min-h-[44px] min-w-[44px] place-items-center rounded-xl border border-gray-200 bg-white text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </button>
              <span className="text-xs font-semibold text-codialis-gray">
                {indexSection + 1} / {sections.length}
              </span>
              <button
                type="button"
                onClick={() => setSectionActive(sections[indexSection + 1]!)}
                disabled={indexSection >= sections.length - 1 || envoi || enregistrement}
                aria-label="Section suivante"
                className="grid min-h-[44px] min-w-[44px] place-items-center rounded-xl border border-gray-200 bg-white text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </button>
              <BoutonMint onClick={valider} disabled={envoi || enregistrement || gesteAbouti}>
                {envoi
                  ? 'Génération du CERFA…'
                  : controle.mode === 'correction' && controle.situation.etat !== 'A_VALIDER'
                    ? 'Corriger et régénérer le CERFA'
                    : 'Valider et générer le CERFA'}
              </BoutonMint>
            </div>
          </div>

          {documentApercu ? (
            <VisionneusePiece
              ouverte
              libellePiece="CERFA d’apprentissage"
              nomFichier="cerfa-apprentissage.pdf"
              urlBase={urlDocumentGenere(documentApercu)}
              onFermer={() => setDocumentApercu(null)}
            />
          ) : null}
        </div>
      )}
    </Modale>
  );
}

/** Une donnée de contexte : libellé discret, valeur lisible, pas de carte. */
function LigneContexte({
  icone,
  libelle,
  valeur,
}: {
  icone: JSX.Element;
  libelle: string;
  valeur: string;
}): JSX.Element {
  return (
    <div className="min-w-0 bg-white px-4 py-3 sm:px-6">
      <dt className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-codialis-gray">
        {icone}
        {libelle}
      </dt>
      <dd className="mt-0.5 truncate text-sm font-semibold text-codialis-navy" title={valeur}>
        {valeur}
      </dd>
    </div>
  );
}

/**
 * Où en est le formulaire, et ce qui l'empêche d'aboutir.
 *
 * Les manques sont **nommés** et cliquables : un « 7 informations manquantes »
 * oblige à parcourir cinq blocs pour trouver lesquelles.
 */
function Avancement({
  manquantes,
  nommees,
  total,
  mode,
  contratValide,
  brouillonEnregistre,
  onAllerAuChamp,
}: {
  manquantes: RubriqueCerfa[];
  /** Les manques tels qu'ils se nomment, ceux du maître regroupés en un seul. */
  nommees: RubriqueCerfa[];
  total: number;
  mode: ControleCerfa['mode'];
  /** Faux pour un contrat né en brouillon depuis le dossier, sans CERFA. */
  contratValide: boolean;
  brouillonEnregistre: boolean;
  onAllerAuChamp: (rubrique: RubriqueCerfa) => void;
}): JSX.Element {
  const remplies = Math.max(0, total - manquantes.length);
  const proportion = total === 0 ? 100 : Math.round((remplies / total) * 100);

  return (
    <div className="space-y-3 border-t border-gray-100 px-4 py-4 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-codialis-navy">
          {manquantes.length === 0 ? (
            <span className="flex items-center gap-2 text-codialis-navy">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-codialis-teal" aria-hidden />
              Toutes les informations obligatoires sont renseignées.
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <CircleAlert className="h-4 w-4 shrink-0 text-red-600" aria-hidden />
              {manquantes.length} information{manquantes.length > 1 ? 's' : ''} obligatoire
              {manquantes.length > 1 ? 's' : ''} à compléter avant la validation
            </span>
          )}
        </p>
        <span className="text-xs font-semibold text-codialis-gray">
          {remplies} / {total} champs obligatoires
        </span>
      </div>

      <div
        className="h-1.5 overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        aria-valuenow={proportion}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Avancement du formulaire"
      >
        <div
          className={`h-full rounded-full transition-all ${manquantes.length === 0 ? 'bg-codialis-mint' : 'bg-codialis-sky'}`}
          style={{ width: `${proportion}%` }}
        />
      </div>

      {nommees.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {nommees.map((rubrique) => (
            <li key={rubrique.cle}>
              <button
                type="button"
                onClick={() => onAllerAuChamp(rubrique)}
                className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs font-semibold text-red-800 transition hover:border-red-300 hover:bg-red-100 [@media(pointer:coarse)]:min-h-[40px]"
              >
                {rubrique.bloc} · {rubrique.libelle}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {mode === 'correction' && !contratValide ? (
        <p className="text-sm text-codialis-gray">
          Ce contrat a été établi depuis le dossier, sans CERFA. Les modifications
          enregistrées rejoignent le contrat ; générer le CERFA le valide.
        </p>
      ) : mode === 'correction' ? (
        <p className="text-sm text-codialis-gray">
          Ce contrat est déjà validé. La correction réécrit le <strong>même</strong> CERFA : le
          dossier apprenant et le dossier entreprise continuent de pointer dessus, et la date de
          transmission est remise à zéro.
        </p>
      ) : brouillonEnregistre ? (
        <p className="text-sm text-codialis-gray">
          Une saisie a déjà été enregistrée pour ce dossier : elle est reprise ci-dessous.
        </p>
      ) : null}
    </div>
  );
}

/** Rang d'affichage d'un bloc, les inconnus en fin de liste. */
function rangBloc(bloc: string): number {
  const rang = ORDRE_BLOCS.indexOf(bloc);
  return rang === -1 ? ORDRE_BLOCS.length : rang;
}

/** Une consigne courte évite d'exposer l'utilisateur à une grille sans contexte. */
function descriptionBloc(bloc: string): string {
  const descriptions: Record<string, string> = {
    Employeur: "Coordonnées et caractéristiques de l'entreprise signataire.",
    'Apprenti(e)': "Identité, coordonnées et situation de l'apprenti(e).",
    'Représentant légal': "Informations requises lorsque l'apprenti(e) est mineur(e).",
    "Maître d'apprentissage": "Personne chargée de l'accompagnement en entreprise.",
    Contrat: 'Dates, durée du travail et conditions du contrat.',
    Formation: 'Diplôme préparé, calendrier et organisation pédagogique.',
    CFA: "Coordonnées officielles reprises automatiquement depuis l'antenne responsable.",
    Dépôt: "Références administratives communiquées par l'organisme de dépôt.",
  };
  return descriptions[bloc] ?? 'Vérifiez les informations avant de poursuivre.';
}

/**
 * La grille de rémunération du CERFA, corrigeable case par case.
 *
 * Le barème la **propose** depuis les dates du contrat et l'âge de
 * l'apprenti(e) ; la seconde tranche d'une année n'existe que lorsqu'un
 * anniversaire fait changer de taux en cours d'année contractuelle. Ce qui est
 * à l'écran est ce qui part au formulaire : le calcul ignore le minimum
 * conventionnel de la branche et les dérogations, que l'assistant(e), elle,
 * connaît. Une grille corrigée n'est jamais recalculée.
 *
 * Les montants sont ceux du serveur, sur le SMIC du référentiel — le même que
 * celui de la rémunération légale du dossier apprenant. Ils se rafraîchissent à
 * l'enregistrement : tant que la correction n'est pas partie, c'est le montant
 * du taux précédent qui reste affiché, et la ligne le dit.
 */
function SectionRemuneration({
  lignes,
  mensuelReference,
  anneesContrat,
  onChanger,
  onRetablir,
}: {
  lignes: LigneRemuneration[];
  /**
   * SMIC mensuel de référence, déjà arrondi par le serveur. L'aperçu « Soit … €
   * brut / mois » n'a plus qu'à y appliquer le pourcentage, à la frappe ; le
   * serveur refait le calcul complet et le fige à l'enregistrement.
   */
  mensuelReference: number | null;
  /** Années couvertes par les dates du contrat : on n'en ajoute pas au-delà. */
  anneesContrat: number;
  onChanger: (lignes: LigneRemuneration[]) => void;
  onRetablir: () => void;
}): JSX.Element {
  const proposee = lignes.length > 0 && lignes.every((ligne) => ligne.proposee);
  const montantDe = (ligne: LigneRemuneration): number | null =>
    ligne.base === 'SMIC' && mensuelReference !== null && ligne.pourcentage > 0
      ? Math.round(mensuelReference * ligne.pourcentage) / 100
      : null;
  const anneesPossibles = ([1, 2, 3, 4] as const).filter(
    (annee) => anneesContrat === 0 || annee <= anneesContrat,
  );

  const modifier = (
    index: number,
    champ: 'dateDebut' | 'dateFin' | 'pourcentage' | 'base',
    valeur: string,
  ): void => {
    const ligne = lignes[index];
    if (!ligne) return;
    const modifiee: LigneRemuneration = {
      ...ligne,
      proposee: false,
      ...(champ === 'pourcentage'
        ? { pourcentage: valeur === '' ? 0 : Math.min(100, Number(valeur)) }
        : champ === 'base'
          ? { base: valeur as LigneRemuneration['base'] }
          : { [champ]: valeur }),
    };
    onChanger(lignes.map((autre, rang) => (rang === index ? modifiee : autre)));
  };

  /** Retire une tranche : le CERFA ne porte que les périodes réelles. */
  const retirer = (index: number): void => {
    onChanger(lignes.filter((_, rang) => rang !== index));
  };

  /**
   * Ajoute une tranche.
   *
   * La seconde tranche d'une année existe quand un anniversaire change le taux
   * en cours d'année ; une année de plus se remplit quand le contrat est
   * prolongé. Le formulaire n'en porte que huit.
   */
  const ajouter = (): void => {
    for (const annee of anneesPossibles) {
      for (const periode of [1, 2] as const) {
        const existe = lignes.some(
          (ligne) => ligne.annee === annee && ligne.periode === periode,
        );
        if (existe) continue;
        onChanger(
          [
            ...lignes,
            {
              annee,
              periode,
              dateDebut: '',
              dateFin: '',
              pourcentage: 0,
              base: 'SMIC' as const,
              proposee: false,
            },
          ].sort((a, b) => a.annee - b.annee || a.periode - b.periode),
        );
        return;
      }
    }
  };

  const champ =
    'min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-codialis-navy outline-none transition focus:border-codialis-sky focus:ring-4 focus:ring-sky-100';

  return (
    <section className="rounded-2xl bg-white shadow-card">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3.5 sm:px-6 sm:py-4">
        <div className="min-w-0">
          <h3 className="font-display text-lg font-bold text-codialis-navy">Rémunération</h3>
          <p className="mt-0.5 text-sm text-codialis-gray">
            {proposee
              ? 'Proposée à partir des dates du contrat, de l’âge de l’apprenti(e) et du barème en vigueur. Corrigez-la si la convention de branche ou une dérogation l’exige.'
              : 'Grille retenue pour ce contrat. C’est elle qui part au formulaire, sans être recalculée.'}
          </p>
        </div>
        {!proposee && lignes.length > 0 ? (
          <BoutonSecondaire onClick={onRetablir}>
            <RotateCcw className="mr-2 h-4 w-4" aria-hidden />
            Rétablir le barème
          </BoutonSecondaire>
        ) : null}
      </header>

      {lignes.length === 0 ? (
        <div className="space-y-3 px-4 py-5 sm:px-6">
          <p className="text-sm text-codialis-gray">
            La grille se calcule dès que les dates du contrat et la date de naissance sont
            connues. Vous pouvez aussi la saisir période par période.
          </p>
          <BoutonSecondaire onClick={ajouter}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Ajouter une période
          </BoutonSecondaire>
        </div>
      ) : (
        <div className="space-y-4 p-4 sm:p-6">
          {lignes.map((ligne, index) => (
            <div
              key={`${ligne.annee}-${ligne.periode}`}
              className="rounded-xl border border-gray-100 bg-codialis-sand/30 p-3 sm:p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold text-codialis-navy">
                  {ligne.annee === 1 ? '1ʳᵉ' : `${ligne.annee}ᵉ`} année · période {ligne.periode}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  {ligne.pourcentageBareme !== null &&
                  ligne.pourcentageBareme !== undefined &&
                  ligne.pourcentageBareme !== ligne.pourcentage ? (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                      Barème : {ligne.pourcentageBareme} %
                    </span>
                  ) : null}
                  {ligne.base === 'MINIMUM_CONVENTIONNEL' ? (
                    <span className="text-xs text-codialis-gray">
                      Montant non calculé : minimum de branche inconnu
                    </span>
                  ) : montantDe(ligne) !== null ? (
                    <span className="text-xs text-codialis-gray" aria-live="polite">
                      Soit{' '}
                      {montantDe(ligne)!.toLocaleString('fr-FR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      € brut / mois
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => retirer(index)}
                    aria-label={`Retirer la période ${ligne.periode} de l’année ${ligne.annee}`}
                    className="grid min-h-[44px] min-w-[44px] place-items-center rounded-xl border border-gray-200 bg-white text-codialis-gray transition hover:border-red-200 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <label className="min-w-0 text-xs font-medium text-codialis-gray">
                  Du
                  <input
                    type="date"
                    value={ligne.dateDebut}
                    onChange={(evenement) => modifier(index, 'dateDebut', evenement.target.value)}
                    className={`mt-1 ${champ}`}
                  />
                </label>
                <label className="min-w-0 text-xs font-medium text-codialis-gray">
                  Au
                  <input
                    type="date"
                    value={ligne.dateFin}
                    onChange={(evenement) => modifier(index, 'dateFin', evenement.target.value)}
                    className={`mt-1 ${champ}`}
                  />
                </label>
                <label className="min-w-0 text-xs font-medium text-codialis-gray">
                  Pourcentage
                  <input
                    type="text"
                    inputMode="numeric"
                    value={ligne.pourcentage ? String(ligne.pourcentage) : ''}
                    onChange={(evenement) =>
                      modifier(index, 'pourcentage', evenement.target.value.replace(/\D/g, ''))
                    }
                    className={`mt-1 ${champ}`}
                  />
                </label>
                <label className="min-w-0 text-xs font-medium text-codialis-gray">
                  Base
                  <select
                    value={ligne.base}
                    onChange={(evenement) => modifier(index, 'base', evenement.target.value)}
                    className={`mt-1 ${champ}`}
                  >
                    <option value="SMIC">du SMIC</option>
                    <option value="MINIMUM_CONVENTIONNEL">du minimum conventionnel</option>
                  </select>
                </label>
              </div>
            </div>
          ))}

          {lignes.length < anneesPossibles.length * 2 ? (
            <BoutonSecondaire onClick={ajouter}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              Ajouter une période
            </BoutonSecondaire>
          ) : null}

          <p className="text-xs text-codialis-gray">
            Le formulaire ne porte que huit tranches : quatre années, deux chacune. Un montant
            au minimum conventionnel n’est pas calculé — la plateforme ne connaît pas le
            salaire minimum de la branche.
          </p>
        </div>
      )}
    </section>
  );
}

/**
 * Aperçu « Durée hebdomadaire : 35 h 00 », sous les deux cases du CERFA.
 *
 * Les cases sont proposées depuis les horaires de la formation (serveur,
 * `domain/cycle-formation.ts`) tant que le contrat ne porte pas sa propre
 * durée ; l'aperçu suit la frappe et dit d'où vient la valeur.
 */
function ApercuDureeHebdo({
  heures,
  minutes,
  formation,
  saisieAuContrat,
}: {
  heures: string;
  minutes: string;
  formation: { heures: number; minutes: number; libelle: string } | null;
  /** La durée est (ou sera) portée par le contrat, et ne suit plus la formation. */
  saisieAuContrat: boolean;
}): JSX.Element {
  const h = heures === '' ? null : Number(heures);
  const m = minutes === '' ? 0 : Number(minutes);
  const lisible = h !== null && Number.isInteger(h) && Number.isInteger(m) && m >= 0 && m < 60;
  const libelle = lisible ? `${h} h ${String(m).padStart(2, '0')}` : null;
  const identique = formation !== null && lisible && formation.heures === h && formation.minutes === m;

  return (
    <div className="rounded-xl bg-codialis-sand px-3.5 py-2.5 text-sm text-codialis-navy md:col-span-2" aria-live="polite">
      {libelle ? (
        <>
          Durée hebdomadaire : <span className="font-bold">{libelle}</span>
        </>
      ) : (
        <span className="text-codialis-gray">Durée hebdomadaire à renseigner.</span>
      )}
      <span className="block text-xs text-codialis-gray">
        {formation === null
          ? 'Les horaires de la formation ne sont pas renseignés dans « Formations & programmes » : la durée se saisit ici.'
          : identique
            ? saisieAuContrat
              ? 'Saisie au contrat, identique aux horaires de la formation.'
              : 'Calculée depuis les horaires de la formation : elle suit leurs changements.'
            : `Diffère des horaires de la formation (${formation.libelle}) : la durée saisie au contrat fait foi.`}
      </span>
    </div>
  );
}

/** Un champ de la saisie, du type que la rubrique déclare. */
function ChampRubrique({
  rubrique,
  aCompleter,
  valeur,
  onChanger,
  onQuitter,
}: {
  rubrique: RubriqueCerfa;
  /** Calculé par l'écran : vide, ou réponse que le dépôt n'accepte pas. */
  aCompleter: boolean;
  valeur: string;
  onChanger: (valeur: string) => void;
  /** Appelée à la sortie du champ quand il pilote ce que le CERFA exige. */
  onQuitter?: () => void;
}): JSX.Element {
  const identifiant = `cerfa-${rubrique.cle.replace(/\./g, '-')}`;
  const aide = rubrique.aide ? `${identifiant}-aide` : undefined;
  const champ = `min-h-[44px] w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-codialis-navy outline-none transition focus:ring-4 ${
    aCompleter
      ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
      : 'border-gray-200 focus:border-codialis-sky focus:ring-sky-100'
  }`;

  return (
    <div className="min-w-0">
      <label
        htmlFor={identifiant}
        className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium text-codialis-navy"
      >
        <span>{rubrique.libelle}</span>
        {/* La quasi-totalité des rubriques du CERFA est exigée : marquer
            l'exception se lit, marquer la règle se retire de la lecture. */}
        {rubrique.requise ? null : (
          <span className="text-xs font-normal text-codialis-gray">facultatif</span>
        )}
      </label>

      {rubrique.saisie === 'choix' && rubrique.options ? (
        <select
          id={identifiant}
          value={valeur}
          onChange={(evenement) => onChanger(evenement.target.value)}
          onBlur={onQuitter}
          aria-invalid={aCompleter || undefined}
          aria-describedby={aide}
          className={`mt-1.5 ${champ}`}
        >
          <option value="">— à renseigner —</option>
          {rubrique.options.map((option) => (
            <option key={option.code} value={option.code}>
              {option.libelle}
            </option>
          ))}
        </select>
      ) : rubrique.saisie === 'booleen' ? (
        <select
          id={identifiant}
          value={valeur}
          onChange={(evenement) => onChanger(evenement.target.value)}
          onBlur={onQuitter}
          aria-invalid={aCompleter || undefined}
          aria-describedby={aide}
          className={`mt-1.5 ${champ}`}
        >
          <option value="">— à renseigner —</option>
          <option value="true">Oui</option>
          <option value="false">Non</option>
        </select>
      ) : (
        <>
          <input
            id={identifiant}
            type={typeHtml(rubrique.saisie)}
            inputMode={rubrique.saisie === 'entier' || rubrique.saisie === 'montant' ? 'decimal' : undefined}
            value={valeur}
            onChange={(evenement) => onChanger(evenement.target.value)}
            onBlur={onQuitter}
            aria-invalid={aCompleter || undefined}
            aria-describedby={aide}
            // La saisie reste libre : les nomenclatures du CERFA concernées ne
            // sont pas arbitrées, et un code inventé vaudrait moins qu'un
            // libellé. La liste propose, elle n'impose pas.
            list={rubrique.suggestions ? `${identifiant}-suggestions` : undefined}
            className={`mt-1.5 ${champ}`}
          />
          {rubrique.suggestions ? (
            <datalist id={`${identifiant}-suggestions`}>
              {rubrique.suggestions.map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>
          ) : null}
        </>
      )}

      {rubrique.aide ? (
        <p id={aide} className="mt-1 text-xs text-codialis-gray">
          {rubrique.aide}
        </p>
      ) : null}
      {rubrique.proposee && valeur === (rubrique.valeur ?? '') ? (
        <p className="mt-1 text-xs text-codialis-gray">
          Proposé depuis la 1ʳᵉ période de la grille de rémunération. Il sera enregistré
          avec le contrat ; corrigez-le si la convention de branche l’exige.
        </p>
      ) : null}
      {aCompleter ? (
        <p className="mt-1 text-xs font-semibold text-red-700">
          {rubrique.valeurAttendue !== undefined && valeur
            ? 'Cette réponse ne permet pas le dépôt du contrat.'
            : 'À compléter avant la validation.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Type de champ HTML.
 *
 * Les montants restent en `text` : `number` masque la virgule sur certains
 * claviers mobiles et transforme un montant saisi en notation scientifique
 * quand la personne colle une valeur.
 */
function typeHtml(saisie: RubriqueCerfa['saisie']): string {
  // Une date ISO est la seule donnée dont le navigateur sait à la fois guider
  // la saisie et garantir le format attendu par le CERFA. Les autres champs
  // restent libres afin de ne pas masquer une valeur métier valide.
  return saisie === 'date' ? 'date' : 'text';
}
